import { z } from 'zod';
/**
 * ParallelCall — a single outbound call placed via the parallel dialer.
 *
 * Lifecycle (9 statuses, enforced by the ParallelCall aggregate on the backend):
 *   dialing → ringing → connected_to_agent → completed
 *                     → voicemail | no_answer | busy | failed | human_detected
 *                       (all terminal branches)
 *
 * After a qualifiable terminal status (completed | voicemail | human_detected),
 * qualify(outcome) sets callOutcome.
 */
export const CallStatusSchema = z.enum([
    'dialing',
    'ringing',
    'connected_to_agent',
    'completed',
    'busy',
    'failed',
    'no_answer',
    'voicemail',
    'human_detected',
]);
/**
 * Les statuts où l'appel est terminé, quelle qu'en soit l'issue.
 *
 * Partagé parce que les deux côtés en dépendent pour la MÊME décision : un
 * rendez-vous part vers le CRM porté par un appel terminé et qualifié. Le
 * serveur le vérifie, et l'écran doit proposer exactement les prospects que
 * le serveur acceptera, sinon il promet un envoi qui n'aura pas lieu.
 */
/**
 * Les statuts pendant lesquels quelqu'un est, ou peut être, en ligne.
 *
 * Partagé pour la même raison que la liste terminale : les deux côtés en
 * dépendent, et une liste recopiée diverge sans que rien ne rougisse. Mesuré
 * le 2026-09-30, le vocabulaire de l'appel était écrit deux fois, dans 35
 * fichiers du front et 11 du serveur.
 */
export const LIVE_CALL_STATUSES = [
    'dialing',
    'ringing',
    'connected_to_agent',
    'human_detected',
];
/**
 * Les résultats qui affirment qu'une conversation a eu lieu. Ce sont eux qui
 * décident du taux de décroché, donc ils ne peuvent pas différer d'un écran
 * à l'autre.
 */
export const CONNECTED_CALL_OUTCOMES = [
    'contacted',
    'connected',
    'meeting_booked',
    'connected_to_agent',
    'not_interested',
    'callback_requested',
];
export const TERMINAL_CALL_STATUSES = [
    'completed',
    'busy',
    'failed',
    'no_answer',
    'voicemail',
    'human_detected',
];
/**
 * CallOutcome is open-ended to tolerate legacy values and custom outcomes
 * configured per tenant. The common ones are listed here but strings
 * outside the union ARE accepted at the schema boundary.
 */
// 2026-05-21 — 'meeting_booked' retiré des outcomes connus : le RDV est
// désormais un drapeau séparé (champ meetingBooked). Reste accepté à la
// frontière via l'union z.string() pour les lignes legacy.
export const KnownCallOutcomeSchema = z.enum([
    'contacted',
    'not_reachable',
    'not_interested',
    'callback',
    'wrong_number',
]);
export const CallOutcomeSchema = z.union([KnownCallOutcomeSchema, z.string()]);
export const SentimentSchema = z.enum(['positive', 'neutral', 'negative']);
export const ParallelCallSchema = z.object({
    id: z.string(),
    clientEmail: z.string().email(),
    prospectName: z.string(),
    prospectPhone: z.string(),
    prospectEmail: z.string().default(''),
    prospectCompany: z.string().default(''),
    prospectTitle: z.string().default(''),
    callStatus: CallStatusSchema,
    callOutcome: CallOutcomeSchema.nullable().default(null),
    // 2026-05-21 — RDV booké = drapeau séparé du callOutcome (un prospet peut
    // être "contacted" ET avoir un RDV). Remplace l'ancienne valeur d'outcome
    // 'meeting_booked' (toujours lue en legacy via hasMeetingBooked()). Les
    // nouveaux RDV posent meetingBooked=true + callOutcome='contacted'.
    meetingBooked: z.boolean().default(false),
    callNotes: z.string().default(''),
    externalCallId: z.string().default(''),
    fromNumber: z.string().default(''),
    startedAt: z.coerce.date().nullable().default(null),
    endedAt: z.coerce.date().nullable().default(null),
    connectedAt: z.coerce.date().nullable().default(null),
    durationSeconds: z.number().int().nonnegative().default(0),
    recordingUrl: z.string().default(''),
    agentName: z.string().default(''),
    agentEmail: z.string().default(''),
    aiSummary: z.string().default(''),
    aiActionItems: z.array(z.string()).default([]),
    aiSentiment: SentimentSchema.nullable().default(null),
    aiConfidence: z.number().default(0),
    followUpAt: z.string().default(''),
    followUpCompleted: z.boolean().default(false),
    followUpNotes: z.string().default(''),
    transcription: z.string().default(''),
    meetingDate: z.string().default(''),
    meetingNotes: z.string().default(''),
    meetingStatus: z.string().default(''),
    calendarEventUrl: z.string().default(''),
    voicemailMode: z.string().default('off'),
    createdDate: z.coerce.date(),
    updatedDate: z.coerce.date(),
});
export const QualifyCallInputSchema = z.object({
    outcome: CallOutcomeSchema,
    notes: z.string().optional(),
    meetingDate: z.string().optional(),
});
//# sourceMappingURL=parallel-call.schema.js.map